@for (section of sections(); track section.id; let first = $first) {
  <div class="nav-group">
    @if (!first) {
      <div class="nav-divider" aria-hidden="true"></div>
    }
    @if (section.heading || section.headingLoading) {
      <h2 class="nav-heading" [id]="headingId(section)">
        @if (section.headingLoading) {
          <span class="nav-heading-placeholder" aria-hidden="true"></span>
          <span class="visually-hidden">Loading organization</span>
        } @else {
          <span class="nav-heading-text" [appTitleIfTruncated]="section.heading ?? ''">{{ section.heading }}</span>
        }
      </h2>
    }
    <ul class="nav-list" [attr.aria-labelledby]="section.heading || section.headingLoading ? headingId(section) : null">
      @for (item of section.items; track item.id) {
        <li class="nav-entry" [class.has-children]="childCount(item) > 0">
          @if (item.route) {
            <a
              class="nav-item"
              [attr.data-item-id]="item.id"
              [routerLink]="item.route"
              routerLinkActive="is-current"
              ariaCurrentWhenActive="page"
              [routerLinkActiveOptions]="matchOptions(item)"
              [appTooltip]="item.label"
              tooltipPosition="right"
              [tooltipDisabled]="!collapsed()"
              (click)="navigate.emit(item)"
              (keydown.escape)="hideTooltips($event)">
              <ng-container [ngTemplateOutlet]="content" [ngTemplateOutletContext]="{ $implicit: item }" />
            </a>
          } @else {
            <button
              type="button"
              class="nav-item"
              [attr.data-item-id]="item.id"
              [appTooltip]="item.label"
              tooltipPosition="right"
              [tooltipDisabled]="!collapsed()"
              (click)="onAction(item)"
              (keydown.escape)="hideTooltips($event)">
              <ng-container [ngTemplateOutlet]="content" [ngTemplateOutletContext]="{ $implicit: item }" />
            </button>
          }
          @if (childCount(item) > 0) {
            @if (item.childrenLabel) {
              <span class="nav-children-caption" [id]="childrenLabelId(item)">{{ item.childrenLabel }}</span>
            }
            <ul class="nav-children" [attr.aria-labelledby]="item.childrenLabel ? childrenLabelId(item) : null">
              @for (child of item.children ?? []; track child.id) {
                <li class="nav-child">
                  <a
                    class="nav-item nav-child-link"
                    [attr.data-child-id]="child.id"
                    [routerLink]="child.route"
                    routerLinkActive="is-current"
                    ariaCurrentWhenActive="page"
                    [routerLinkActiveOptions]="childMatch"
                    [appTooltip]="hasRichTooltip(item, child) ? childTooltip : child.label"
                    tooltipPosition="right"
                    [tooltipDisabled]="hasRichTooltip(item, child) ? false : !collapsed()"
                    [tooltipMaxWidth]="child.previewUrl ? 'calc(200px + 1.5rem + 4px)' : '200px'"
                    (keydown.escape)="hideTooltips($event)">
                    @if (child.loading) {
                      <span class="nav-icon nav-child-icon nav-child-spinner" aria-hidden="true"></span>
                    } @else if (child.tag) {
                      <span class="nav-icon nav-child-icon nav-child-tag" [style.background]="child.tag.color" aria-hidden="true">{{ child.tag.text }}</span>
                    } @else if (child.icon) {
                      <svg class="nav-icon nav-child-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false">
                        @for (d of child.icon; track $index) {
                          <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="d" />
                        }
                      </svg>
                    }
                    <span class="nav-label">{{ child.label }}</span>
                    @if (child.loading) {
                      <span class="visually-hidden">, {{ child.loadingLabel ?? 'Loading' }}</span>
                    }
                    @if (child.dotLabel) {
                      <span class="nav-child-dot" aria-hidden="true"></span>
                      <span class="visually-hidden">, {{ child.dotLabel }}</span>
                    }
                    @if (child.status; as status) {
                      <span class="nav-child-status" [attr.data-kind]="status.kind" aria-hidden="true">
                        @if (status.kind === 'progress') {
                          <svg viewBox="0 0 16 16" focusable="false" [class.is-indeterminate]="status.percent == null">
                            <circle class="nav-child-status-track" cx="8" cy="8" r="6" />
                            <circle class="nav-child-status-arc" cx="8" cy="8" r="6" pathLength="100" [attr.stroke-dasharray]="ringDash(status)" />
                          </svg>
                        } @else {
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" focusable="false">
                            @for (d of statusIcon(status); track $index) {
                              <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="d" />
                            }
                          </svg>
                        }
                      </span>
                      <span class="visually-hidden">, {{ status.label }}</span>
                    }
                  </a>
                  <ng-template #childTooltip>
                    <div class="nav-preview">
                      @if (childTooltipCaption(item, child)) {
                        <span class="nav-preview-caption">{{ childTooltipCaption(item, child) }}</span>
                      }
                      @if (child.previewUrl) {
                        <img class="nav-preview-img" [src]="child.previewUrl" alt="" />
                      }
                      <span class="nav-preview-label">{{ child.label }}</span>
                      @if (child.status) {
                        <span class="nav-preview-status">{{ child.status.label }}</span>
                      }
                    </div>
                  </ng-template>
                  @if (child.closeLabel && !collapsed()) {
                    <button type="button" class="nav-child-close" [attr.data-child-id]="child.id" [attr.aria-label]="child.closeLabel" (click)="closeChild(item, child, $event)">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                      </svg>
                    </button>
                  }
                </li>
              }
            </ul>
          }
        </li>
      }
    </ul>
  </div>
}

<ng-template #content let-item>
  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false">
    @for (d of item.icon; track $index) {
      <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="d" />
    }
  </svg>
  <span class="nav-label">{{ item.label }}</span>
  @if (hasBadge(item)) {
    <span class="nav-badge" aria-hidden="true">{{ badgeText(item) }}</span>
    <span class="visually-hidden">, {{ item.badgeLabel ?? item.badge }}</span>
  }
</ng-template>
